/***
*
*   PROMOTION CODES PANEL
*   Manage promotion codes linked to coupons
*
**********/

import { useState } from 'react';
import { Card, Button, Icon, Badge } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

export function PromotionCodesPanel({ promotionCodes, coupons, onSave, onDelete, loading }) {
  const [editingPromo, setEditingPromo] = useState(null);
  const [isNewPromo, setIsNewPromo] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const promoList = promotionCodes || {};
  const promoKeys = Object.keys(promoList);
  const couponsList = coupons || {};

  const handleAddPromo = () => {
    setEditingPromo({
      id: '',
      code: '',
      coupon: '',
      active: true,
      customer: null,
      expires_at: null,
      max_redemptions: null,
      restrictions: {
        first_time_transaction: false,
        minimum_amount: null,
        minimum_amount_currency: 'usd'
      }
    });
    setIsNewPromo(true);
  };

  const handleEditPromo = (promoKey) => {
    const promo = promoList[promoKey];
    setEditingPromo({ ...promo });
    setIsNewPromo(false);
  };

  const handleCancelEdit = () => {
    setEditingPromo(null);
    setIsNewPromo(false);
  };

  const handleSavePromo = () => {
    if (!editingPromo.code || !editingPromo.coupon) {
      return;
    }

    onSave(editingPromo, isNewPromo);
    setEditingPromo(null);
    setIsNewPromo(false);
  };

  const handleFieldChange = (field, value) => {
    setEditingPromo(prev => ({ ...prev, [field]: value }));
  };

  const handleRestrictionChange = (field, value) => {
    setEditingPromo(prev => ({
      ...prev,
      restrictions: {
        ...prev.restrictions,
        [field]: value
      }
    }));
  };

  const getPromoStatus = (promo) => {
    if (!promo.active) return { text: 'Inactive', color: 'gray' };
    if (promo.expires_at && new Date(promo.expires_at * 1000) < new Date()) {
      return { text: 'Expired', color: 'red' };
    }
    if (promo.max_redemptions && promo.times_redeemed >= promo.max_redemptions) {
      return { text: 'Fully Redeemed', color: 'orange' };
    }
    return { text: 'Active', color: 'green' };
  };

  const getCouponName = (couponId) => {
    if (typeof couponId === 'object' && couponId.id) {
      return couponsList[couponId.id]?.name || couponId.name || couponId.id;
    }
    return couponsList[couponId]?.name || couponId;
  };

  const getCouponDiscount = (couponId) => {
    let coupon;
    if (typeof couponId === 'object' && couponId.id) {
      coupon = couponId;
    } else {
      coupon = couponsList[couponId];
    }

    if (!coupon) return 'N/A';

    if (coupon.percent_off) {
      return `${coupon.percent_off}% off`;
    }
    if (coupon.amount_off) {
      const symbol = coupon.currency === 'usd' ? '$' : coupon.currency?.toUpperCase();
      return `${symbol}${(coupon.amount_off / 100).toFixed(2)} off`;
    }
    return 'No discount';
  };

  const filteredPromos = promoKeys.filter(key => {
    const promo = promoList[key];
    const code = (promo.code || '').toLowerCase();
    const id = (promo.id || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return code.includes(search) || id.includes(search);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <Icon image="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder="Search promotion codes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          text="Add New Promo Code"
          icon="plus"
          color="green"
          disabled={true}
          tooltip="Adding new promotion codes is disabled. Manage codes directly in Stripe."
          action={handleAddPromo}
        />
      </div>

      {/* Promo Editor */}
      {editingPromo && (
        <Card>
          <div className={Style.settingsSection}>
            <h3 className={Style.settingsHeader}>
              {isNewPromo ? 'Add New Promotion Code' : 'Edit Promotion Code'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Promo Code *</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingPromo.code}
                  onChange={(e) => handleFieldChange('code', e.target.value.toUpperCase())}
                  placeholder="SUMMER2024"
                />
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Linked Coupon *</label>
                <select
                  className={Style.settingsInput}
                  value={typeof editingPromo.coupon === 'object' ? editingPromo.coupon.id : editingPromo.coupon}
                  onChange={(e) => handleFieldChange('coupon', e.target.value)}
                >
                  <option value="">Select a coupon...</option>
                  {Object.keys(couponsList).map(couponKey => {
                    const coupon = couponsList[couponKey];
                    return (
                      <option key={coupon.id} value={coupon.id}>
                        {coupon.name} ({getCouponDiscount(coupon.id)})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className={Style.settingsCard}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingPromo.active}
                    onChange={(e) => handleFieldChange('active', e.target.checked)}
                    className="w-4 h-4 text-brand-500 rounded border-slate-300 focus:ring-brand-500"
                  />
                  <span className={Style.settingsLabel}>Active</span>
                </label>
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Max Redemptions</label>
                <input
                  type="number"
                  className={Style.settingsInput}
                  value={editingPromo.max_redemptions ?? ''}
                  onChange={(e) => handleFieldChange('max_redemptions', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Unlimited"
                  min="1"
                />
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Expires At</label>
                <input
                  type="datetime-local"
                  className={Style.settingsInput}
                  value={editingPromo.expires_at ? new Date(editingPromo.expires_at * 1000).toISOString().slice(0, 16) : ''}
                  onChange={(e) => handleFieldChange('expires_at', e.target.value ? Math.floor(new Date(e.target.value).getTime() / 1000) : null)}
                />
              </div>

              <div className={Style.settingsCard}>
                <label className={Style.settingsLabel}>Specific Customer ID</label>
                <input
                  type="text"
                  className={Style.settingsInput}
                  value={editingPromo.customer ?? ''}
                  onChange={(e) => handleFieldChange('customer', e.target.value || null)}
                  placeholder="cus_xxxxx"
                />
              </div>

              {/* Restrictions */}
              <div className={Style.settingsCard + " md:col-span-2 lg:col-span-3"}>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Restrictions</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPromo.restrictions?.first_time_transaction || false}
                        onChange={(e) => handleRestrictionChange('first_time_transaction', e.target.checked)}
                        className="w-4 h-4 text-brand-500 rounded border-slate-300 focus:ring-brand-500"
                      />
                      <span className="text-sm text-slate-600">First-time customers only</span>
                    </label>
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Minimum Amount (cents)</label>
                    <input
                      type="number"
                      className={Style.settingsInput}
                      value={editingPromo.restrictions?.minimum_amount ?? ''}
                      onChange={(e) => handleRestrictionChange('minimum_amount', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="10000"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Currency</label>
                    <select
                      className={Style.settingsInput}
                      value={editingPromo.restrictions?.minimum_amount_currency || 'usd'}
                      onChange={(e) => handleRestrictionChange('minimum_amount_currency', e.target.value)}
                    >
                      <option value="usd">USD</option>
                      <option value="eur">EUR</option>
                      <option value="gbp">GBP</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                text="Cancel"
                color="gray"
                outline
                action={handleCancelEdit}
              />
              <Button
                text={isNewPromo ? 'Create Promo Code' : 'Save Changes'}
                color="green"
                loading={loading}
                action={handleSavePromo}
                disabled={!editingPromo.code || !editingPromo.coupon}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Promo Codes Grid */}
      <div className={Style.addOnsGrid}>
        {filteredPromos.length > 0 ? (
          filteredPromos.map((promoKey) => {
            const promo = promoList[promoKey];
            const status = getPromoStatus(promo);

            return (
              <Card key={promoKey} className={Style.addOnCard}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={Style.addOnName}>{promo.code}</h4>
                      <Badge text={status.text} color={status.color} />
                    </div>
                    <p className="text-xs text-slate-400 font-mono">{promo.id}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      icon="edit-2"
                      size="small"
                      color="blue"
                      outline
                      disabled={true}
                      tooltip="Editing promo codes is disabled. Manage in Stripe."
                      action={() => handleEditPromo(promoKey)}
                    />
                    <Button
                      icon="trash-2"
                      size="small"
                      color="red"
                      outline
                      disabled={true}
                      tooltip="Deleting promo codes is disabled. Manage in Stripe."
                      action={() => onDelete(promo.id)}
                    />
                  </div>
                </div>

                {/* Linked Coupon */}
                <div className="mb-3 p-3 bg-brand-50 rounded-lg border border-brand-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon image="link" size={14} color="blue" />
                    <span className="text-xs font-medium text-slate-600">Linked Coupon</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">{getCouponName(promo.coupon)}</p>
                  <p className="text-sm text-green-600">{getCouponDiscount(promo.coupon)}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Icon image="repeat" size={14} color="gray" />
                    <span>
                      Redeemed: {promo.times_redeemed || 0}
                      {promo.max_redemptions ? ` / ${promo.max_redemptions}` : ' / ∞'}
                    </span>
                  </div>

                  {promo.expires_at && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icon image="calendar" size={14} color="gray" />
                      <span>Expires: {new Date(promo.expires_at * 1000).toLocaleString()}</span>
                    </div>
                  )}

                  {promo.customer && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icon image="user" size={14} color="gray" />
                      <span>Customer: {promo.customer}</span>
                    </div>
                  )}

                  {promo.restrictions && Object.keys(promo.restrictions).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 font-medium mb-1">Restrictions:</p>
                      {promo.restrictions.minimum_amount && (
                        <p className="text-xs text-slate-600">
                          Min: ${(promo.restrictions.minimum_amount / 100).toFixed(2)}
                        </p>
                      )}
                      {promo.restrictions.first_time_transaction && (
                        <p className="text-xs text-slate-600">First-time customers only</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400">
                    Created: {new Date(promo.created * 1000).toLocaleDateString()}
                  </p>
                </div>
              </Card>
            );
          })
        ) : (
          <Card>
            <div className={Style.emptyState}>
              <Icon image="hash" size={48} className="mx-auto mb-4 text-slate-300" />
              <p className={Style.emptyStateText}>
                {searchTerm ? `No promo codes found matching "${searchTerm}"` : 'No promotion codes configured yet'}
              </p>
              {!searchTerm && (
                <Button
                  text="Add Your First Promo Code"
                  icon="plus"
                  color="green"
                  disabled={true}
                  tooltip="Adding new promotion codes is disabled. Manage codes directly in Stripe."
                  action={handleAddPromo}
                />
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}